import { Component, ErrorHandler, OnInit, ViewChild } from '@angular/core';
import { NavigationEnd, NavigationExtras, Router, ActivatedRoute } from '@angular/router';
import { AutoResume, DEFAULT_INTERRUPTSOURCES, Idle } from '@ng-idle/core';
import { ModalDirective } from 'ngx-bootstrap/modal';
import { ErrorCatchingService } from './core/error-catching.service';
import { UserService } from './core/user/user.service';
import { Title } from '@angular/platform-browser';
import { ToastContainerDirective, ToastrService } from 'ngx-toastr';
import { filter, map, mergeMap } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
/**
 * Top-most component for the entire application.
 */

declare let ga: Function;

@Component({
    // standalone: true,
    // imports: [NgFor],
    selector: 'my-app',
    templateUrl: 'app.component.html',
    styleUrls: ['app.component.scss']
})

export class AppComponent extends ErrorHandler implements OnInit {

    @ViewChild('errorModal', {static: true}) public errorModal: ModalDirective;
    @ViewChild('forbiddenModal', {static: false}) public forbiddenModal: ModalDirective;
    @ViewChild('idleModal', {static: false}) public idleModal: ModalDirective;
    @ViewChild(ToastContainerDirective, {static: false}) public toastContainer: ToastContainerDirective;

    private defaultTitle: string = 'Remedi SeniorCare';
    errorTitle: string;
    errorMessages: string[] = [];

    /** Show warning after 17 minutes */
    readonly idleWarningAfter = 17 * 60;
    /** Warn for 3 minutes before auto-logout */
    readonly autoLogoutPeriod = 3 * 60;
    /** Seconds until user will be auto logged out */
    autoLogoutRemaining: number;

    /**
     * Construct.
     */
    constructor(private errorSvc: ErrorCatchingService,
                private router: Router,
                private idle: Idle,
                private userSvc: UserService,
                private activatedRoute: ActivatedRoute,
                private titleService: Title,
                private toastr: ToastrService) {
        super();

        this.configIdleWarning();
    }

    public ngOnInit() {
        console.log('app.component loading');

        
        this.toastr.overlayContainer = this.toastContainer;
        this.errorSvc.error$.subscribe((error) => this.showError(error));
        this.errorModal.onHide.subscribe(()=>this.errorMessages=[]);          
        //Source: https://toddmotto.com/dynamic-page-titles-angular-2-router-events
        this.router.events
            .pipe(filter((event) => event instanceof NavigationEnd),map(() => this.activatedRoute),
                map((route) => {
                    while (route.firstChild) route = route.firstChild;
                    return route;
                }), filter((route) => route.outlet === 'primary'),
             mergeMap((route) => route.data))
            .subscribe((event) => this.titleService.setTitle(event['title'] || this.defaultTitle));
    }

    /**
     * Ask the user to log back in.
     */
    relogin() {
        this.idleModal.hide();
        this.errorModal.hide();

        // Pass the current URL for redirecting
        let navExtras: NavigationExtras = {
            queryParams: {
                redirectUrl: this.router.url,
                logout: "Please log back in."
            }
        };

        // Navigate to the login page
        this.router.navigate(['/login'], navExtras);
    }

    ////////////////// Error Modal /////////////////////

    /**
     * Open a popup to display an unhandled error.
     */
    private showError(error: Error) {
        if (error instanceof HttpErrorResponse){
            let httpError = error;
            if(httpError.status === 401) {
                this.relogin();
            }
            if(httpError.status === 403){

                if (!this.forbiddenModal.isShown) {
                    this.forbiddenModal.show();
                }
            }
        }
        else {
            //We don't want the user to see these
            //IE-11 drug price quote throws this on first access for some users
            if (error.name === "TypeError") {
                console.log(error.name + ': ' + error.message);
                return;
            }

            this.errorMessages.push(error.message);

            if (!this.errorModal.isShown) {
                this.errorTitle = error.name;
                this.errorModal.show();
            }
        }
    }

    doNothing(): void {
        /*
         * This method is called on any click in the application due to a bug in Safari Mobile 7.0+ where non interactive items ignore fired events. This makes it difficult to close the facility selector
         * See the following link from Mozilla docs: https://developer.mozilla.org/en-US/docs/Web/Events/click#Safari_Mobile
         */
    }

    /**
     * Close the error modal.
     */
    closeError() {
        this.errorModal.hide();
        this.errorTitle = "";
        this.errorMessages = [];
    }

    /**
     * Close the error modal.
     */
    closeForbidden() {
        this.forbiddenModal.hide();
    }

    /**
     * Hide the idle modal.
     */
    HideIdleModal() {
        this.idleModal.hide();
    }

    //////////////// Idle Warning Modal //////////////////

    /**
     * Configure idle timeout handling on page load.
     */
    private configIdleWarning() {
        // Configure idle processing
        this.idle.setIdle(this.idleWarningAfter);
        this.idle.onIdleStart
            .subscribe(() => this.showIdleWarning());

        // Configure timeout processing
        this.idle.setTimeout(this.autoLogoutPeriod);
        this.idle.onTimeoutWarning
            .subscribe((countdown) => this.autoLogoutRemaining = countdown);
        this.idle.onTimeout
            .subscribe(() => this.relogin());

        // Configure interrupts
        this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);
        this.idle.setAutoResume(AutoResume.notIdle);

        // Activate / deactivate upon user login/logout
        this.userSvc.user$.subscribe((user) => {
            if (user)
                this.idle.watch();
            else
                this.idle.stop();
        });
    }

    /**
     * Show the idle warning dialog. Triggered by ng-idle's onIdleStart event.
     */
    private showIdleWarning() {
        if (!this.idleModal.isShown) {
            this.idleModal.show();
        }
    }

    /**
     * Idle warning dialog has been hidden.
     */
    onIdleWarningHide() {
        if (this.userSvc.isAuthenticated)
            this.idle.watch();
    }
}
